import { useEffect, useRef } from 'react';
import { MatchStage } from '@signalvote/shared';
import {
  advanceVisual,
  compareCells,
  FIELD_COLUMNS,
  FieldCell,
  SCAN_SWEEP,
  simulatedCell,
  visualMoment,
  type Field,
} from '@/lib/fundraising/enrichmentField';

/**
 * What the field is showing: people being added (the first `added` dots filled in, the rest outlined),
 * the matcher at work on the current row, or the finished field sorted by outcome.
 */
export type FieldMode =
  | { kind: 'adding'; added: number; stopped: boolean }
  | { kind: 'matching'; step: MatchStage | null }
  | { kind: 'done' };

const DOT_RATIO = 0.73;
const SORT_DELAY = 0.2;
const SORT_DURATION = 1.3;

const ease = (x: number) => (x <= 0 ? 0 : x >= 1 ? 1 : x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);

interface Palette {
  giving: string;
  confirm: string;
  failed: string;
  seg: string;
  primary: string;
}

function palette(el: Element): Palette {
  const css = getComputedStyle(el);
  const read = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback;
  return {
    giving: read('--color-success', '#1e7350'),
    confirm: read('--color-warning', '#92551a'),
    failed: read('--color-error', '#c0392b'),
    seg: read('--color-border', '#e5e7eb'),
    primary: read('--color-primary', '#0095e7'),
  };
}

function colour(cell: FieldCell, p: Palette): string {
  switch (cell) {
    case FieldCell.Giving:
      return p.giving;
    case FieldCell.ToConfirm:
      return p.confirm;
    case FieldCell.CouldntCheck:
      return p.failed;
    case FieldCell.Checking:
      return p.primary;
    case FieldCell.NoRecord:
    case FieldCell.Waiting:
      return p.seg;
  }
}

const reducedMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The list as a field of dots, drawn on a canvas so hundreds of dots can move without re-rendering. The
 * motion is calm: a scan line over the row being matched, lighting dots as it passes (a simulation —
 * see `simulatedCell`; the row takes its batch's real colours when the batch finishes), a slow pulse
 * while the AI judge decides, and at the end the dots settling into order by outcome. Under reduced
 * motion it draws each state still.
 */
export function EnrichmentFieldCanvas({ field, mode, label }: { field: Field; mode: FieldMode; label: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const latest = useRef({ field, mode });
  latest.current = { field, mode };

  // The current row's visual clock (see `advanceVisual`), and when the field was sorted while on screen;
  // a field opened already finished shows it still.
  const visual = useRef({ row: -1, vt: 0, at: 0, idle: 0 });
  const sortedAt = useRef<number | null>(null);
  const wasDone = useRef<boolean | null>(null);

  const modeKey = JSON.stringify(mode);
  const now = () => performance.now() / 1000;
  const done = mode.kind === 'done';
  if (wasDone.current === false && done) sortedAt.current = now();
  if (wasDone.current === null || !done) sortedAt.current = done ? -Infinity : null;
  wasDone.current = done;

  useEffect(() => {
    const el = canvas.current;
    const ctx = el?.getContext('2d');
    if (!el || !ctx) return;
    const still = reducedMotion();
    let colours = palette(el);
    let coloursAt = now();
    let frame = 0;

    const draw = () => {
      const { field: f, mode: m } = latest.current;
      const t = now();
      if (t - coloursAt > 1) {
        colours = palette(el);
        coloursAt = t;
      }
      const width = el.clientWidth || 592;
      const cell = width / FIELD_COLUMNS;
      const dot = cell * DOT_RATIO;
      const inset = (cell - dot) / 2;
      const height = f.rows.length * cell;
      const dpr = window.devicePixelRatio || 1;
      if (el.width !== Math.round(width * dpr) || el.height !== Math.round(height * dpr)) {
        el.width = Math.round(width * dpr);
        el.height = Math.round(height * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);
      const radius = Math.min(2.5, dot / 3);

      const flat = f.rows.flatMap((row, r) => row.cells.map((c, col) => ({ c, r, col })));
      const sorting = m.kind === 'done';
      const target = new Map<number, number>();
      if (sorting) {
        flat
          .map((d, i) => ({ c: d.c, i }))
          .sort((a, b) => compareCells(a.c, b.c) || a.i - b.i)
          .forEach((d, k) => target.set(d.i, k));
      }
      const sortT = sortedAt.current === null ? 0 : still ? Infinity : t - sortedAt.current;
      const added = m.kind === 'adding' ? m.added : Infinity;
      // The current row plays its sweeps on its own clock, reset as each row starts.
      const currentRow = f.rows.findIndex((r) => r.state === 'current');
      const clock = visual.current;
      if (clock.row !== currentRow) Object.assign(clock, { row: currentRow, vt: 0, at: t, idle: 0 });
      const realStep = m.kind === 'matching' ? m.step : null;
      const dt = Math.min(0.5, t - clock.at);
      const vt = advanceVisual(clock.vt, still ? Infinity : dt, realStep);
      // Held at the real step: the scan line keeps looping from where it got to.
      clock.idle = vt === clock.vt ? clock.idle + dt : 0;
      clock.vt = vt;
      clock.at = t;
      const { step: visualStep, elapsed } = visualMoment(clock.vt);

      flat.forEach((d, i) => {
        let x = d.col * cell + inset;
        let y = d.r * cell + inset;
        let alpha = 1;
        let fill = colour(d.c, colours);
        let glow = 0;
        const row = f.rows[d.r];

        if (m.kind === 'adding' && i >= added) {
          ctx.globalAlpha = 1;
          ctx.shadowBlur = 0;
          ctx.strokeStyle = m.stopped && i === Math.floor(added) ? colours.failed : colours.seg;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.roundRect(x + 0.5, y + 0.5, dot - 1, dot - 1, radius);
          ctx.stroke();
          return;
        }

        let shown = d.c;
        if (row.state === 'current' && d.c === FieldCell.Checking && m.kind === 'matching') {
          const sim = simulatedCell(d.r, d.col, visualStep, elapsed);
          shown = sim.cell;
          fill = colour(shown, colours);
          if (shown !== FieldCell.Checking && shown !== FieldCell.Waiting && shown !== FieldCell.NoRecord) {
            glow = Math.max(0, 1 - sim.since / 0.5);
          }
        }
        if (shown === FieldCell.Waiting) alpha = m.kind === 'adding' ? 1 : 0.55;
        if (shown === FieldCell.Checking) {
          alpha = visualStep === MatchStage.Judging && m.kind === 'matching' && !still ? 0.45 + 0.3 * Math.sin(t * 5 + d.col * 1.7) : 0.35;
        }

        if (sorting) {
          const k = target.get(i)!;
          const tx = (k % FIELD_COLUMNS) * cell + inset;
          const ty = Math.floor(k / FIELD_COLUMNS) * cell + inset;
          const order = [FieldCell.Giving, FieldCell.ToConfirm, FieldCell.CouldntCheck, FieldCell.NoRecord].indexOf(d.c);
          const delay = SORT_DELAY + Math.max(0, order) * 0.12 + (k / flat.length) * 0.5;
          const p = ease((sortT - delay) / SORT_DURATION);
          x += (tx - x) * p;
          y += (ty - y) * p;
          if (d.c === FieldCell.NoRecord) alpha = 1 - 0.6 * p;
          if (p > 0 && p < 1 && d.c !== FieldCell.NoRecord) glow = Math.sin(p * Math.PI) * 0.6;
        }

        let size = dot;
        if (glow > 0) {
          ctx.shadowColor = fill;
          ctx.shadowBlur = 8 * glow;
          size += 2 * glow;
          x -= glow;
          y -= glow;
        } else {
          ctx.shadowBlur = 0;
        }
        ctx.globalAlpha = alpha;
        ctx.fillStyle = fill;
        ctx.beginPath();
        ctx.roundRect(x, y, size, size, radius);
        ctx.fill();
      });
      ctx.shadowBlur = 0;

      // The scan line sweeps the row being searched and matched; the AI judge's turn gets a quiet band.
      if (m.kind === 'matching' && currentRow >= 0 && realStep !== null) {
        const y = currentRow * cell;
        if (visualStep === MatchStage.Judging) {
          ctx.globalAlpha = 0.07;
          ctx.fillStyle = colours.primary;
          ctx.fillRect(0, y, width, cell);
        } else if (!still) {
          const x = (((elapsed + clock.idle) % SCAN_SWEEP) / SCAN_SWEEP) * width;
          ctx.save();
          ctx.beginPath();
          ctx.rect(Math.max(0, x - 60), y, Math.min(60, x), cell);
          ctx.clip();
          const trail = ctx.createLinearGradient(x - 60, 0, x, 0);
          trail.addColorStop(0, 'rgba(0,0,0,0)');
          trail.addColorStop(1, colours.primary);
          ctx.globalAlpha = 0.22;
          ctx.fillStyle = trail;
          ctx.fillRect(x - 60, y, 60, cell);
          ctx.restore();
          ctx.globalAlpha = 1;
          ctx.fillStyle = colours.primary;
          ctx.shadowColor = colours.primary;
          ctx.shadowBlur = 8;
          ctx.fillRect(x - 1, y - 2, 2, cell + 4);
          ctx.shadowBlur = 0;
        }
      }
      ctx.globalAlpha = 1;

      if (!still) frame = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(frame);
    // Redrawn on every change when still; otherwise the loop reads the latest props each frame.
  }, [field, modeKey]);

  return (
    <canvas
      ref={canvas}
      role="img"
      aria-label={label}
      className="block w-full"
      style={{ aspectRatio: `${FIELD_COLUMNS} / ${Math.max(1, field.rows.length)}` }}
    />
  );
}
